import { DEFAULT_POINT_ICON_KEY, DEFAULT_POINT_ICON_URL, lookAtRangeOf } from './kmlHelpers';

/**
 * Applies KML-derived styles to the map's Data layer and re-applies them on every zoom
 * change (for LOD-based Point visibility). Returns a cleanup function that removes the
 * zoom listener — call it when the component unmounts or the KML changes.
 *
 * @param iconTintMap - Optional map from tint key ("<url>|<RRGGBB>|<opacity>") to a
 *   pre-fetched Blob URL of the tinted icon image.  Built by the caller before rendering
 *   so that tinted icons are shown from the first render without a flash of the original.
 * @param previewPointIds - When provided, only Point features whose GeoJSON id is in this
 *   set are shown. Used to limit the small preview to the easternmost and westernmost
 *   icons so the preview is not cluttered with dense overlapping markers.
 * @param iconScaleMultiplier - Global scale factor applied on top of each icon's
 *   KML-specified scale. Defaults to 1. Pass e.g. 0.6 to shrink icons in the preview.
 */
export function applyKmlStyles(
  map: google.maps.Map,
  iconTintMap?: ReadonlyMap<string, string>,
  previewPointIds?: ReadonlySet<number>,
  iconScaleMultiplier = 1
): () => void {
  const styleFeature = (feature: google.maps.Data.Feature): google.maps.Data.StyleOptions => {
    // Respect KML <visibility>0</visibility>.
    const visibility = feature.getProperty('visibility') as number | undefined;
    if (visibility === 0) return { visible: false };

    const strokeColor = feature.getProperty('stroke') as string | undefined;
    const strokeWeight = feature.getProperty('stroke-width') as number | undefined;
    const strokeOpacity = feature.getProperty('stroke-opacity') as number | undefined;
    const fillColor = feature.getProperty('fill') as string | undefined;
    const fillOpacity = feature.getProperty('fill-opacity') as number | undefined;
    const iconUrl = feature.getProperty('icon') as string | undefined;
    const iconColor = feature.getProperty('icon-color') as string | undefined;
    const folderPaletteColor = feature.getProperty('folderPaletteColor') as string | undefined;
    const iconOpacity = (feature.getProperty('icon-opacity') as number | undefined) ?? 1;
    // togeojson exposes <IconStyle><scale> as "icon-scale"
    const iconScale = feature.getProperty('icon-scale') as number | undefined;

    if (feature.getGeometry()?.getType() === 'Point') {
      // KmlLayer renders the icon URL even when <IconStyle><scale> is 0, treating it
      // as default scale (1).  We replicate that: iconScale=0 falls back to scale=1
      // so the custom icon is still shown; the feature is never hidden purely because
      // the KML author set scale=0 (that only affects the icon size hint, not visibility).

      // In preview mode, hide all Point features except the extreme east/west ones.
      if (previewPointIds && !previewPointIds.has(feature.getId() as number)) return { visible: false };

      // KmlLayer hides detail-level Point markers (small LookAt range) until zoomed in.
      // Features with a range > 800m are "overview" level and always visible.
      // Features with a range <= 800m use minZoom = ceil(log2(7000 / range)) — calibrated
      // so that a 290m range feature appears at zoom 5, matching KmlLayer behaviour.
      const lookAtRange = lookAtRangeOf(feature);
      if (!isNaN(lookAtRange) && lookAtRange <= 800) {
        const minZoom = Math.min(14, Math.max(0, Math.ceil(Math.log2(7000 / lookAtRange))));
        if ((map.getZoom() ?? 0) < minZoom) return { visible: false };
      }
    }

    // Resolve stroke colour in priority order:
    //   1. Explicit KML <LineStyle><color> → strokeColor (set by togeojson)
    //   2. <LineStyle> present but no <color> (togeojson set stroke-width only) →
    //      use the folder-palette colour injected by annotateLineFolderColors, which
    //      approximates KmlLayer's per-folder cycling behaviour.
    //   3. No <LineStyle> at all → KML_DEFAULT_STROKE global fallback.
    const KML_DEFAULT_STROKE = '#8a79ee';
    const geomType = feature.getGeometry()?.getType();
    const resolvedStrokeColor =
      strokeColor ??
      (geomType !== 'Point' && geomType !== undefined ? (folderPaletteColor ?? KML_DEFAULT_STROKE) : undefined);

    const style: google.maps.Data.StyleOptions = {
      ...(resolvedStrokeColor && { strokeColor: resolvedStrokeColor }),
      ...(strokeWeight !== undefined && { strokeWeight }),
      ...(strokeOpacity !== undefined && { strokeOpacity }),
      ...(fillColor && { fillColor }),
      ...(fillOpacity !== undefined && { fillOpacity })
    };

    if (feature.getGeometry()?.getType() === 'Point') {
      if (iconUrl && /^(https?|blob:|data:)/i.test(iconUrl)) {
        // Apply <IconStyle><scale> so icons render at the KML-specified size rather than
        // their natural image dimensions. Google Maps KML shape PNGs are 64×64px at scale=1.
        // iconScale=0 is treated as default (1) — KmlLayer renders the icon at its natural
        // size regardless of a zero scale value.
        const scale = (iconScale !== undefined && iconScale > 0 ? iconScale : 1) * iconScaleMultiplier;
        const px = Math.round(64 * scale);

        // Upgrade http://maps.google.com/ → https:// to avoid mixed-content blocking in
        // production (HTTPS) environments.  All Google Maps static files are served over HTTPS.
        // Resolve tinted icon URL when a colour is set.  iconTintMap holds pre-fetched Blob
        // URLs keyed by "<originalUrl>|<RRGGBB>|<opacity>" (see MapDocPreviewer).  Fall back
        // to the original URL if no tint map was provided or the entry is missing.
        let resolvedUrl = iconUrl.replace(/^http:\/\/maps\.google\.com\//i, 'https://maps.google.com/');
        // data: and blob: URLs are embedded/local — skip server-side tinting (the API
        // cannot fetch them). Apply scale only; the image renders as-is.
        if (
          !iconUrl.startsWith('blob:') &&
          !iconUrl.startsWith('data:') &&
          iconColor &&
          iconColor !== '#ffffff' &&
          iconTintMap
        ) {
          const hexColor = iconColor.replace('#', '');
          const key = `${iconUrl}|${hexColor}|${iconOpacity.toFixed(4)}`;
          resolvedUrl = iconTintMap.get(key) ?? resolvedUrl;
        }

        style.icon = {
          url: resolvedUrl,
          scaledSize: new google.maps.Size(px, px)
        };
      } else {
        // No valid icon URL (undefined or a relative path from a KMZ that cannot be
        // resolved in isolation). Use the pre-tinted default icon from the tint map,
        // falling back to the raw (white) paddle URL if the tint hasn't loaded yet.
        const defaultUrl = iconTintMap?.get(DEFAULT_POINT_ICON_KEY) ?? DEFAULT_POINT_ICON_URL;
        // The wht-circle paddle is 64×64 px. Apply a 0.7 base scale so the default
        // icon is noticeably smaller than a full-size custom icon, then apply the
        // caller-supplied multiplier (0.6 in preview mode, 1 in full view).
        const px = Math.round(64 * 0.55 * iconScaleMultiplier);
        style.icon = {
          url: defaultUrl,
          scaledSize: new google.maps.Size(px, px)
        };
      }
    }

    return style;
  };

  map.data.setStyle(styleFeature);
  const zoomListener = map.addListener('zoom_changed', () => map.data.setStyle(styleFeature));
  return () => zoomListener.remove();
}
